import React from 'react';
import { Link } from 'react-router-dom';
import { X, Star, Users, BarChart3, GitCompareArrows, Trash2, Eye, ExternalLink } from 'lucide-react';

export const QuestionnaireDetailModal = ({
  questionnaire,
  statsData,
  statsLoading,
  showPreview, setShowPreview,
  selectedForCompare, setSelectedForCompare,
  onClose,
  onDelete,
  user
}) => {
  if (!questionnaire) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="questionnaire-detail-modal">
      <div className="bg-[#0B132B] rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto m-4">
        <div className="sticky top-0 bg-[#0B132B] border-b border-white/10 p-6 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-semibold text-white">{questionnaire.fonction}</h3>
            <p className="text-sm text-slate-400 mt-1">
              {questionnaire.niveau} • {questionnaire.type_questionnaire}
              {questionnaire.client && ` • ${questionnaire.client}`}
              {questionnaire.ref_ao && ` • AO : ${questionnaire.ref_ao}`}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white ml-4" data-testid="close-modal-button">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {statsLoading ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-slate-400">Chargement des statistiques...</p>
            </div>
          ) : statsData && (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white/5 rounded-lg p-4" data-testid="stat-tests-count">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                      <BarChart3 size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Tests passés</p>
                      <p className="text-2xl font-bold text-white">{statsData.total_tests}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/5 rounded-lg p-4" data-testid="stat-avg-score">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500/15 rounded-lg flex items-center justify-center">
                      <Star size={20} className="text-[#F59E0B]" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Note moyenne</p>
                      <p className="text-2xl font-bold text-white">{statsData.average_score.toFixed(1)}/5</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Candidates List */}
              {statsData.candidates.length > 0 ? (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-slate-400" />
                      <h4 className="text-sm font-semibold text-white">Candidats testés</h4>
                    </div>
                    {selectedForCompare.length >= 2 && (
                      <Link
                        to={`/comparer?ids=${selectedForCompare.join(',')}`}
                        className="text-xs font-medium text-white bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg hover:bg-white/10 flex items-center gap-1"
                        data-testid="compare-button"
                      >
                        <GitCompareArrows size={14} />
                        Comparer ({selectedForCompare.length})
                      </Link>
                    )}
                  </div>
                  <div className="space-y-2">
                    {statsData.candidates.map((candidate) => (
                      <div
                        key={candidate.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedForCompare.includes(candidate.id) ? 'border-[#0B132B] bg-white/5' : 'border-white/10 hover:border-[#6C757D]'
                        }`}
                        onClick={() => {
                          setSelectedForCompare(prev =>
                            prev.includes(candidate.id)
                              ? prev.filter(id => id !== candidate.id)
                              : [...prev, candidate.id]
                          );
                        }}
                        data-testid={`candidate-${candidate.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-white">
                              {candidate.prenom} {candidate.nom}
                            </p>
                            <p className="text-xs text-slate-400">
                              {new Date(candidate.date).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <Star key={s} size={14}
                                  fill={s <= Math.round(candidate.score || 0) ? '#F59E0B' : '#1E293B'}
                                  stroke={s <= Math.round(candidate.score || 0) ? '#F59E0B' : '#1E293B'}
                                />
                              ))}
                            </div>
                            <span className="text-sm font-medium text-white">
                              {candidate.score.toFixed(1)}/5
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 border border-white/10 rounded-lg mb-6">
                  <Users size={32} className="text-[#E9ECEF] mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Aucun test passé pour ce questionnaire</p>
                </div>
              )}

              {/* Admin Actions */}
              {user?.role === 'admin' && (
                <div className="flex gap-2 mb-6">
                  <button
                    onClick={() => onDelete(questionnaire.id)}
                    className="flex items-center gap-1.5 text-xs text-[#EF4444] border border-[#EF4444]/30 px-3 py-1.5 rounded-lg hover:bg-[#EF4444]/5 transition-colors"
                    data-testid="delete-questionnaire-button"
                  >
                    <Trash2 size={14} />
                    Supprimer
                  </button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors border ${
                    showPreview
                      ? 'bg-[#0B132B] text-white border-[#0B132B]'
                      : 'bg-[#0B132B] text-white border-white/10 hover:bg-white/5'
                  }`}
                  data-testid="preview-questions-button"
                >
                  <Eye size={18} />
                  Visualiser
                </button>
                <Link
                  to={`/lancer-test/${questionnaire.id}`}
                  className="flex-1 eva-btn-primary text-center block"
                  data-testid="modal-launch-test-button"
                >
                  Utiliser pour un nouveau test
                </Link>
              </div>

              {/* Questions Preview */}
              {showPreview && questionnaire.questions && (
                <div className="mt-6 space-y-4" data-testid="questions-preview">
                  <h4 className="text-sm font-semibold text-white uppercase tracking-wider">
                    Aperçu des questions ({questionnaire.questions.length})
                  </h4>
                  {questionnaire.questions.map((question, i) => (
                    <div key={question.id || `q-${i}`} className="border border-white/10 rounded-lg p-4" data-testid={`preview-question-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-slate-400">Q{i + 1}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#3B82F6]/10 text-[#3B82F6]">
                          QCM
                        </span>
                        {question.tags && question.tags.length > 0 && (
                          <div className="flex gap-1 ml-auto">
                            {question.tags.slice(0, 2).map((tag, j) => (
                              <span key={`tag-${i}-${j}`} className="px-1.5 py-0.5 bg-white/5 text-[10px] text-slate-400 rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-white mb-3">{question.text}</p>
                      {question.choices && question.choices.length > 0 && (
                        <div className="bg-white/5 rounded-md p-3">
                          <ul className="space-y-1.5">
                            {question.choices.map((choice, j) => (
                              <li key={`choice-${i}-${j}`} className={`text-xs flex items-start gap-2 ${
                                choice.is_correct ? 'text-[#10B981] font-medium' : 'text-slate-300'
                              }`}>
                                <span className="mt-0.5 shrink-0">{choice.is_correct ? '✓' : '○'}</span>
                                <span>{choice.text}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
